import { debounce } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react'
import { Breadcrumb, Button, Card, Col, Container, Form, Modal, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Groups } from '../../api/Groups';
import { Pathologies } from '../../api/Pathologies';
import { Users } from '../../api/Users';
import AppLoader from '../../components/AppLoader';
import { useAuth } from '../../contexts/auth';
// import { useAuth } from '../../contexts/auth';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { GroupModel } from '../../models/Group';
import { Pathology } from '../../models/Pathology';
import { Caregiver, User, UserDTO, UserRole } from '../../models/User';
import { Patients } from '../../api/Patients';

export default function PatientEdit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setPatients] = useState<UserDTO>();
  const [groups, setGroups] = useState<GroupModel[]>();
  const [pathologies, setPathologies] = useState<Pathology[]>();
  const [userGroup, setUserGroup] = useState<GroupModel>();
  // const { loggedUser } = useAuth();

  const { showLoader, hideLoader } = useFullScreenLoader();
  const { showToast } = useToasts();
  const [oldUsername, setOldUserName] = useState<String>('');
  const [oldCaregiverUsername, setOldCaregoverUserName] = useState<String>('');
  const [available, setAvailable] = useState<boolean>(true);
  const [caregiverAvailable, setCaregiverAvailable] = useState<boolean>(true);
  const [validated, setValidated] = useState<boolean>();
  const [acceptableTX, setAcceptableTX] = useState<boolean>(true);
  const { loggedUser } = useAuth();

  const [showPasswordModal, setShowPasswordModal] = useState<boolean>(false);

  useEffect(() => {
    if (id) initPatients();
    else setPatients(new UserDTO());

    initPathologies();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (loggedUser) initGroups();
    // eslint-disable-next-line
  }, [loggedUser]);

  useEffect(() => {
    if (user && !id) {
      const generatedName = user.firstname.length && user.lastname.length ? `${user!.firstname.toLowerCase()}.${user!.lastname.toLowerCase()}`.trim().replaceAll(/\s/g, ".") : "";

      setPatients({
        ...user!,
        username: generatedName,
      });
    }
    // eslint-disable-next-line
  }, [user?.lastname, user?.firstname]);

  useEffect(() => {
    if (userGroup) {
      setPatients({
        ...user!,
        groups: [userGroup],
      });
    }
    // eslint-disable-next-line
  }, [userGroup]);


  const handlePasswordModalClose = () => setShowPasswordModal(false);
  const openChangePasswordModal = () => () => {
    setShowPasswordModal(true);
  }


  const checkUsernameAvailability = () => {
    if (user?.username !== oldUsername) {
      Users.usernameAvailability(user!.username)
        .then(
          ({ data: availability }) => setAvailable(availability.available),
          (err) => {
            const message = err.response?.data?.message || err.message;
            const status = err.response?.status;
            showToast!('user-error', message, `User Error ${status ? `(${status})` : ''}`, 'danger');
          }
        )
    }
  }

  const checkCaregiverUsernameAvailability = () => {
    if (user?.caregiver?.username !== oldCaregiverUsername) {
      Users.usernameAvailability(user!.caregiver!.username!)
        .then(
          ({ data: availability }) => setCaregiverAvailable(availability.available),
          (err) => {
            const message = err.response?.data?.message || err.message;
            const status = err.response?.status;
            showToast!('caregiver-error', message, `Caregiver Error ${status ? `(${status})` : ''}`, 'danger');
          }
        )
    }
  }

  const debouncedChangeHandler = useMemo(() => debounce(checkUsernameAvailability, 500), [user]);
  const debouncedCaregiverChangeHandler = useMemo(() => debounce(checkCaregiverUsernameAvailability, 500), [user]);

  useEffect(() => {
    if (user?.username) debouncedChangeHandler();
  }, [user?.username, debouncedChangeHandler]);

  useEffect(() => {
    if (user?.caregiver?.username) debouncedCaregiverChangeHandler();
  }, [user?.caregiver?.username, debouncedCaregiverChangeHandler]);

  useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel();
      debouncedCaregiverChangeHandler.cancel();
    }
  }, [debouncedChangeHandler, debouncedCaregiverChangeHandler]);

  const initPatients = () => {
    Patients.getPatient(id!)
      .then(
        ({ data: user }) => {
           setPatients(new UserDTO(user)); 
           setOldUserName(user.username)
          if(user.caregiver){
            setOldCaregoverUserName(user.caregiver.username!)
          }
          },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast!('user-error', message, `User Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }

  const initGroups = () => {
    Groups.getGroups()
      .then(
        ({ data: groups }) => {
          if (loggedUser!.role === UserRole.DOCTOR) setGroups(groups.filter(group => loggedUser!.groups!.map((g) => g.id).includes(group.id!)));

          else setGroups(groups)
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast!('groups-error', message, `Groups Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const initPathologies = () => {
    Pathologies.getPathologies()
      .then(
        ({ data: pathologies }) => setPathologies(pathologies),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast!('pathologies-error', message, `Pathologies Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const cancel = () => navigate('/patients');

  const handleSubmit = (evt: React.FormEvent<HTMLFormElement>) => {
    const form = evt.currentTarget;
    evt.stopPropagation();
    evt.preventDefault();
    setValidated(true);
    console.log(user)
    console.log(form)
    if (form.checkValidity() === true) {
      showLoader!();

      let userDto: any = { ...user }
      userDto.groups = userDto.groups!.map((g: any) => g.id)
      if (userDto.caregiver.username === "" || userDto.caregiver.password === "" || userDto.caregiver.email === "") {

        delete userDto.caregiver
      }

      const promise = id
        ? Patients.updatePatient(userDto as User)
        : Patients.createPatient(userDto as User);

      promise.then(
        () => {
          hideLoader!();
          showToast!('patient-success', `User ${id ? 'updated' : 'created'} successfully`, 'Success', 'success');
          navigate('/patients');
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          hideLoader!();
          showToast!('patient-error', message, `User Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
    }
  }


  const changePassword = () => {
    console.log(user)
    const newPass = { id: user?.id, password: user?.password }
    showLoader!();
    Patients.updatePatient(newPass as User).then(() => {
      hideLoader!();
      handlePasswordModalClose()
      showToast!('patient-success', `User passowrd changed successfully`, 'Success', 'success');
      navigate('/patients');
    },
      (err) => {
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        handlePasswordModalClose()
        hideLoader!();
        showToast!('patient-error', message, `User Error ${status ? `(${status})` : ''}`, 'danger');
      })

  }


  const handleFieldChange = (field: keyof User) => (evt: React.ChangeEvent<any>) => {
    const value = evt.target.value;
    if (field === 'taxCode') {
      value.match(/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/gi) ? setAcceptableTX(true) : setAcceptableTX(false);
    }
    console.log(field)
    console.log(value)
    console.log(Number(value))
    setPatients({
      ...user!,
      [field]: field === 'groups' ? [value] : field === 'pathology' ? Number(value) : value,
    })
  }
  const handleCaregiverFieldChange = (field: keyof Caregiver) => (evt: React.ChangeEvent<any>) => {
    const value = evt.target.value;
    setPatients({
      ...user!,
      caregiver: { ...user!.caregiver, [field]: value }

    })
  }

  const handleUserGroupChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    let correspondingGroup = groups?.filter((g) => g.id === Number(value))!
    setUserGroup(correspondingGroup[0]);
  }

  const handleGroupChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    setPatients({
      ...user!,
      groups: evt.target.checked ? [...user!.groups!.concat(groups?.filter((g) => g.id === Number(value))!)] : user?.groups!.filter(group => group.id !== Number(value)),
    });
  }

  const renderUserForm = () => {
    return (
      <>
        <div className="border-bottom py-2 px-3 d-flex justify-content-between">
          <Card.Title className="mb-0">{id ? user?.firstname + " " + user?.lastname : t('patientEdit.title')}</Card.Title>
          {id && <Button variant="warning" className="border" size="sm" onClick={openChangePasswordModal()}>Change Password</Button>}
        </div>

        <Form validated={validated} noValidate onSubmit={handleSubmit}>

          <Card.Body>
            <h6>Clinical Data</h6>
            <Card className="p-3 border-0">
              <Row className="gx-2">
                <Col md={6}>
                  <Form.Group controlId="user-firstname" className="mb-3">
                    <Form.Label>{t('userEdit.firstname')}</Form.Label>
                    <Form.Control size="sm" placeholder={t('patientEdit.placeholders.firstname')} value={user?.firstname} onChange={handleFieldChange('firstname')} required />
                    <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: t('userEdit.firstname') })}</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="user-lastname" className="mb-3">
                    <Form.Label>{t('userEdit.lastname')}</Form.Label>
                    <Form.Control size="sm" placeholder={t('patientEdit.placeholders.lastname')} value={user?.lastname} onChange={handleFieldChange('lastname')} required />
                    <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: t('userEdit.lastname') })}</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="user-email" className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control size="sm" placeholder={t('patientEdit.placeholders.email')} value={user?.email} onChange={handleFieldChange('email')} type="email" required />
                    <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: "Email" })}</Form.Control.Feedback>
                  </Form.Group>
                </Col>




                {user?.role === UserRole.PATIENT && (
                  <>
                    <Col md={6}>
                      <Form.Group controlId="user-tax-code" className="mb-3">
                        <Form.Label>{t('userEdit.taxCode')}</Form.Label>
                        <Form.Control size="sm" placeholder={t('patientEdit.placeholders.taxCode')} value={user?.taxCode} onChange={handleFieldChange('taxCode')} required />
                        <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: t('userEdit.taxCode') })}</Form.Control.Feedback>
                        {user?.taxCode && !acceptableTX && <Form.Control.Feedback type="invalid" className="d-block">{t('userEdit.taxCodeWrong')}</Form.Control.Feedback>}
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group controlId="user-pathology" className="mb-3">
                        <Form.Label>{t('userEdit.pathology')}</Form.Label>
                        <Form.Select size="sm" value={user?.pathology || ""} onChange={handleFieldChange('pathology')}>
                          <option value="">{t('userEdit.selectPathology')}</option>
                          {pathologies?.map(pathology => (
                            <option key={pathology.id} value={pathology.id}>{pathology.name}</option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: t('userEdit.pathology') })}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </>
                )}

              </Row>

              {user?.role === UserRole.DOCTOR && (
                <Form.Group controlId="user-group" className="mb-3">
                  <Form.Label>{t('userEdit.group')}</Form.Label>
                  <Row className="gx-2 align-items-center">
                    {groups?.map(group => (
                      <Col key={group.id} md={3}>
                        <Form.Check
                          id={String(group.id)}
                          type="checkbox"
                          label={group.name}
                          checked={user.groups?.map((g: any) => g.id).includes(group.id!)}
                          value={group.id!}
                          onChange={handleGroupChange}
                        />
                      </Col>
                    ))}
                  </Row>
                </Form.Group>
              )}

              {user?.role === UserRole.PATIENT && (
                <Form.Group controlId="user-group" className="mb-3">
                  <Form.Label>{t('userEdit.group')}</Form.Label>
                  <Row className="gx-2 align-items-center">
                    {groups?.map(group => (
                      <Col key={group.id} md={3}>
                        <Form.Check
                          id={String(group.id)}
                          type="radio"
                          label={group.name}
                          name="patient-group"
                          value={group.id}
                          checked={user.groups?.map((g: any) => g.id).includes(group.id!)}
                          onChange={handleUserGroupChange}
                        />
                      </Col>
                    ))}
                  </Row>
                  <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: t('userEdit.group') })}</Form.Control.Feedback>
                </Form.Group>
              )}
            </Card>

            <h6>Account Data</h6>
            <Card className="p-3 border-0">
              <Row>
                <Col md={6}>
                  <Form.Group controlId="user-username" className="mb-3">
                    <Form.Label>Username</Form.Label>
                    <Form.Control placeholder={t('patientEdit.placeholders.username')} size="sm" value={user?.username} onChange={handleFieldChange('username')} required />
                    <Form.Control.Feedback type="invalid">{t('errors.requiredField', { field: "Username" })}</Form.Control.Feedback>
                    {user?.username && !available && <Form.Control.Feedback type="invalid" className="d-block">{t('userEdit.usernameUnavailable')}</Form.Control.Feedback>}
                  </Form.Group>
                </Col>

                {!id &&
                  <Col md={6}>
                    <Form.Group controlId="user-password" className="mb-3">
                      <Form.Label>Password</Form.Label>
                      <Form.Control size="sm" placeholder={t('doctorEdit.placeholders.password')} value={user?.password} onChange={handleFieldChange('password')} type="text" isInvalid={(user?.password?.length! > 0 && user?.password?.length! < 8) ? true : false} />
                      <Form.Control.Feedback type="invalid">{t('errors.password8Char', { field: "password" })}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                }
              </Row>
            </Card>


            <h6>Caregiver</h6>
            <Card className="p-3 border-0">
              <Row>
                <Col md={6}>
                  <Form.Group controlId="caregiver-username" className="mb-3">
                    <Form.Label>{t('patientEdit.placeholders.caregiverusername')}</Form.Label>
                    <Form.Control placeholder={t('patientEdit.placeholders.caregiverusername')} size="sm" value={user?.caregiver?.username} onChange={handleCaregiverFieldChange('username')} />
                    {user?.caregiver?.username && !caregiverAvailable && <Form.Control.Feedback type="invalid" className="d-block">{t('userEdit.usernameUnavailable')}</Form.Control.Feedback>}
                  </Form.Group>
                </Col>

                {!id &&
                  <Col md={6}>
                    <Form.Group controlId="caregiver-password" className="mb-3">
                      <Form.Label>{t('patientEdit.placeholders.caregiverpassword')}</Form.Label>
                      <Form.Control placeholder={t('patientEdit.placeholders.caregiverpassword')} size="sm" value={user?.caregiver?.password} onChange={handleCaregiverFieldChange('password')} type="text" isInvalid={(user?.caregiver?.password?.length! > 0 && user?.caregiver?.password?.length! < 8) ? true : false} />
                      <Form.Control.Feedback type="invalid">{t('errors.password8Char', { field: "password" })}</Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                }

                <Col md={6}>
                  <Form.Group controlId="caregiver-name" className="mb-3">
                    <Form.Label>{t('patientEdit.placeholders.caregivername')}</Form.Label>
                    <Form.Control placeholder={t('patientEdit.placeholders.caregivername')} size="sm" value={user?.caregiver?.firstname} onChange={handleCaregiverFieldChange('firstname')} />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="caregiver-surname" className="mb-3">
                    <Form.Label>{t('patientEdit.placeholders.caregiversurname')}</Form.Label>
                    <Form.Control placeholder={t('patientEdit.placeholders.caregiversurname')} size="sm" value={user?.caregiver?.lastname} onChange={handleCaregiverFieldChange('lastname')} />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="caregiver-phone" className="mb-3">
                    <Form.Label>{t('patientEdit.placeholders.caregiverphone')}</Form.Label>
                    <Form.Control placeholder={t('patientEdit.placeholders.caregiverphone')} size="sm" value={user?.caregiver?.phone} onChange={handleCaregiverFieldChange('phone')} />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group controlId="caregiver-email" className="mb-3">
                    <Form.Label>{t('patientEdit.placeholders.caregiveremail')}</Form.Label>
                    <Form.Control placeholder={t('patientEdit.placeholders.caregiveremail')} type="email" size="sm" value={user?.caregiver?.email} onChange={handleCaregiverFieldChange('email')} />
                  </Form.Group>
                </Col>


              </Row>
            </Card>

          </Card.Body>

          <div className="d-flex justify-content-between border-top py-2 px-3">
            <Button variant="light" className="border" size="sm" onClick={cancel}>{t('common.cancel')}</Button>
            <Button variant="success" type="submit" size="sm">{t('common.confirm')}</Button>
          </div>
        </Form>

        <Modal show={showPasswordModal} onHide={handlePasswordModalClose}>
          <Modal.Header closeButton>
            <Modal.Title>{t('common.changePassword')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Col md={12}>
              <Form.Group controlId="user-password" className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control size="sm" placeholder={t('doctorEdit.placeholders.password')} value={user?.password} onChange={handleFieldChange('password')} type="text" isInvalid={(user?.password?.length! > 0 && user?.password?.length! < 8) ? true : false} />
                <Form.Control.Feedback type="invalid">{t('errors.password8Char', { field: "password" })}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="light" className="border" size="sm" onClick={handlePasswordModalClose}>{t('common.cancel')}</Button>
            <Button variant="success" size="sm" onClick={changePassword} disabled={user?.password?.length! < 8 ? true : false}>{t('common.confirm')}</Button>
          </Modal.Footer>
        </Modal>
      </>
    )
  }

  const renderAsyncContent = () => {
    if (id && !user) return (
      <div className="d-flex justify-content-center py-3">
        <AppLoader size={150} />
      </div>
    )

    return loggedUser && renderUserForm();
  }

  return (
    <Container>
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/patients' }}>{t('pages.patients')}</Breadcrumb.Item>
        <Breadcrumb.Item active>{id ? `${user?.firstname! + ' ' + user?.lastname!}` : t('patientEdit.currentPage')}</Breadcrumb.Item>
      </Breadcrumb>

      <Card>{renderAsyncContent()}</Card>
    </Container>
  )
}